import { useState } from 'react';
import key from 'weak-key';
import { Container, Row } from '@geberit/gdds';

// styles
import styles from './text-media-column.module.scss';

// types
import type { TextMediaColumnProps } from '.';

// components
import { SectionClosingLine } from '../Common/section-closing-line';
import Column from './Views/column';
import { Headline } from 'components/ContentElementsGdds/headline/headline';
import VideoLightbox from 'components/lightbox/video-lightbox';

// utils
import { classNameBuilder } from 'utils/classNameBuilder';
import { decodingContent } from 'utils/decodingContent';
import { buildSize, gridSizes } from 'utils/gridSize';
import { useSectionId } from 'utils/hooks/use-section-id';
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';

type ModalVideo = VideoProp & { playerControl: boolean; showLink: boolean };

export function TextMediaColumn({
  subtitle,
  title,
  contentIndex,
  titleInAnchor,
  columns,
  anchor,
  border,
  childOf,
}: Readonly<TextMediaColumnProps>) {
  const [modalOpen, setModalOpen] = useState<boolean | ModalVideo>(false);
  const sectionId = useSectionId(title, anchor ?? '', titleInAnchor, contentIndex);

  const isChildOfAccordionOrTab = childOf && ['tab', 'accordion'].includes(childOf);

  const toggleVideoModal = (videoItem: ModalVideo) => {
    setModalOpen(videoItem);
  };

  return (
    <div
      className={classNameBuilder(styles.textMediaColumnWrapper, border && styles[border])}
      id={sectionId}
    >
      <Container maxContentWidth={buildSize(gridSizes.gddsFullGrid)}>
        <Headline
          isFlexItem
          title={decodingContent(title)}
          subtitle={decodingContent(subtitle)}
          tag={isChildOfAccordionOrTab ? Formats.h4 : Formats.h2}
          variant={Formats.h2}
          className={styles.textMediaColumnHeadline}
          titlePreviewId="#st_title"
          subtitlePreviewId="#st_subtitle"
        />
        <Row>
          {columns?.map((column, index) => (
            <Column
              {...column}
              toggleVideoModal={toggleVideoModal}
              key={key(column)}
              previewId={['#st_leftColumn', '#st_rightColumn'][index]!}
              childOf={childOf}
            />
          ))}
        </Row>
      </Container>
      {!border && <SectionClosingLine />}
      {modalOpen && (
        <VideoLightbox
          onClose={toggleVideoModal}
          videoId={(modalOpen as ModalVideo).videoId}
          isYoutubeVideo={(modalOpen as ModalVideo).type === 'youtube'}
          playerControl={(modalOpen as ModalVideo).playerControl}
          showLink={(modalOpen as ModalVideo).showLink}
        />
      )}
    </div>
  );
}
